/**
 * Testi italiani del dominio progetti: elenco, panoramica, dipendenze, accessi.
 *
 * Due gruppi vanno letti con attenzione perché il testo **è** il modello di
 * sicurezza, non il suo contorno:
 *
 *  - `links.intro` dice che una dipendenza dichiarata non concede nulla;
 *  - `grants.intro` dice che un agente di un altro progetto vede questo solo se
 *    compare nell'elenco degli accessi.
 *
 * Il grassetto dentro questi due testi è portante: toglierlo cambia ciò che
 * l'utente capisce di un'azione che concede permessi. Sono resi con `Trans`, a
 * cui il componente passa l'elemento `<strong>`: react-i18next costruisce veri
 * elementi React dal marcatore, e in tutta l'applicazione non c'è un solo
 * `dangerouslySetInnerHTML`.
 */

/** Testi del dominio progetti. */
export const projects = {
  /** Stato di un progetto, come etichetta e come voce di filtro. */
  status: {
    /** Progetto in uso. */
    active: 'Attivo',
    /** Progetto temporaneamente fermo. */
    suspended: 'Sospeso',
    /** Progetto uscito dall'uso corrente. */
    archived: 'Archiviato',
  },

  /** Azione concessa da un permesso, come etichetta. */
  action: {
    /** Sola lettura. */
    read: 'Lettura',
    /** Lettura e scrittura. */
    write: 'Scrittura',
    /** Amministrazione del progetto. */
    admin: 'Amministrazione',
  },

  /** La stessa azione dentro una frase, dove va in minuscolo. */
  actionInline: {
    /** Sola lettura. */
    read: 'lettura',
    /** Lettura e scrittura. */
    write: 'scrittura',
    /** Amministrazione del progetto. */
    admin: 'amministrazione',
  },

  /** Elenco dei progetti accessibili. */
  list: {
    /** Titolo della pagina. */
    title: 'Progetti',
    /** Frase che dice a che cosa serve la pagina. */
    description: 'I progetti a cui hai accesso. Apri un progetto per gestirlo.',
    /** Pulsante che apre il cassetto di creazione. */
    create: 'Nuovo progetto',
    /** Nome accessibile del campo di ricerca. */
    searchLabel: 'Cerca fra i progetti',
    /** Testo di riempimento del campo di ricerca. */
    searchPlaceholder: 'Cerca per nome o slug',
    /** Stato vuoto per chi può creare un progetto. */
    empty: 'Nessun progetto accessibile.',
    /** Stato vuoto per chi non può crearne. */
    emptyReadOnly: "Nessun progetto accessibile. Chiedi l'accesso a un amministratore.",
    /** Azione proposta nello stato vuoto. */
    emptyAction: 'Crea il primo progetto',
    /** Tabella vuota perché la ricerca non trova nulla. */
    noMatch: 'Nessun progetto corrisponde alla ricerca.',
    /** Lettura dell'elenco non riuscita. */
    loadFailed: 'Caricamento dei progetti non riuscito. Riprova.',
    /** Colonna del set che ospita il progetto. */
    hostColumn: 'Set',
  },

  /** Pagina di un progetto. */
  page: {
    /** Titolo mostrato per un progetto inesistente o non accessibile. */
    notFoundTitle: 'Progetto non trovato',
    /** Spiegazione sotto il titolo. */
    notFoundSubtitle:
      "Il progetto non esiste più oppure non hai accesso. Torna all'elenco e scegline un altro.",
    /** Lettura dei permessi non riuscita. */
    grantsLoadFailed: 'Caricamento degli accessi non riuscito. Riprova.',
    /** Schede della pagina. */
    tabs: {
      /** Dati del progetto. */
      overview: 'Panoramica',
      /** Strumenti collegati al progetto. */
      tools: 'Strumenti',
      /** Dipendenze dichiarate. */
      links: 'Dipendenze',
      /** Permessi concessi. */
      grants: 'Accessi',
      /** Requisiti funzionali (ADR 0050 §6). */
      requirements: 'Requisiti funzionali',
    },
    /** Archiviazione del progetto. */
    archive: {
      /** Etichetta del pulsante. */
      label: 'Archivia',
      /** Domanda della conferma, con il nome del progetto. */
      question: 'Archiviare {{name}}?',
      /** Che cosa succede davvero. */
      consequence:
        "Il progetto esce dall'uso corrente e non si riattiva da qui. Dipendenze e permessi restano registrati.",
      /** Esito riuscito. */
      done: 'Progetto archiviato.',
      /** Esito fallito. */
      failed: 'Archiviazione non riuscita. Riprova.',
    },
  },

  /** Cancellazione di un progetto, riservata agli amministratori. */
  delete: {
    /** Etichetta del pulsante e della conferma. */
    label: 'Elimina',
    /** Titolo della finestra. */
    question: 'Eliminare {{name}}?',
    /** Che cosa succede davvero. */
    consequence:
      'Il progetto viene cancellato con le sue dipendenze, i permessi e le credenziali. Non si annulla.',
    /** Nome del form di conferma. */
    form: 'Conferma della cancellazione',
    /** Etichetta del campo di conferma. */
    confirmLabel: 'Per confermare scrivi lo slug: {{slug}}',
    /** Esito riuscito. */
    done: 'Progetto {{name}} eliminato.',
    /** Esito fallito. */
    failed: 'Cancellazione non riuscita. Riprova.',
  },

  /** Scheda «Panoramica». */
  overview: {
    /** Titolo della scheda. */
    title: 'Panoramica',
    /** Etichetta del set che ospita il progetto. */
    hostSet: 'Set che lo ospita',
    /** Etichetta della data di archiviazione. */
    archivedAt: 'Archiviato il',
    /** Etichetta del repository git. */
    repository: 'Repository git',
    /** Nessun repository impostato. */
    repositoryNone: 'Nessun repository',
    /** Pulsante che apre il cassetto del repository. */
    repositoryEdit: 'Modifica repository',
    /** Cassetto del repository. */
    repositoryForm: {
      /** Titolo del cassetto. */
      title: 'Repository git',
      /** Pulsante di invio. */
      submit: 'Salva repository',
      /** Etichetta del campo. */
      url: 'Indirizzo https',
      /** Aiuto sotto il campo. */
      help: 'Serve a un secondo set di agenti per clonare il progetto. Solo https, senza utente né token. Lascia vuoto per toglierlo.',
      /** Esito riuscito. */
      done: 'Repository salvato.',
      /** Esito fallito. */
      failed: 'Salvataggio non riuscito. Riprova.',
      /** Indirizzo rifiutato. */
      invalid: 'Indirizzo non valido. Usa un indirizzo https senza utente né token.',
      /** Permesso mancante. */
      forbidden: 'Per cambiare il repository devi amministrare questo progetto.',
    },
  },

  /** Cassetto di creazione di un progetto. */
  form: {
    /** Titolo del cassetto e nome accessibile del form. */
    title: 'Nuovo progetto',
    /** Pulsante di invio. */
    submit: 'Crea progetto',
    /** Etichetta del campo del nome. */
    name: 'Nome',
    /** Etichetta del campo dello slug. */
    slug: 'Slug',
    /** Vincoli dello slug, sotto il campo. */
    slugHelp:
      'Da 3 a 64 caratteri: minuscole, cifre e trattini. Deve iniziare con una lettera. Non si può cambiare dopo la creazione.',
    /** Etichetta del campo della descrizione. */
    description: 'Descrizione',
    /** Etichetta del campo del repository. */
    repository: 'Repository git (https)',
    /** Aiuto sotto il campo del repository. */
    repositoryHelp:
      'Facoltativo. Serve a un secondo set di agenti per clonare il progetto. Solo https, senza utente né token.',
    /** Esito riuscito. */
    created: 'Progetto creato.',
    /** Esito fallito. */
    failed: 'Creazione non riuscita. Riprova.',
  },

  /** Campo «Indirizzo del sito» dei tre punti di creazione (ADR 0054). */
  subdomain: {
    /** Etichetta del campo. */
    label: 'Indirizzo del sito',
    /** Aiuto sotto il campo. */
    help: "È l'indirizzo pubblico del sito. La demo aggiunge «-demo». Non si può cambiare dopo la creazione.",
    /** Indirizzo di produzione risultante. */
    prodHost: 'Produzione: {{host}}',
    /** Indirizzo della demo risultante. */
    demoHost: 'Demo: {{host}}',
    /** Controllo in corso. */
    checking: "Controllo dell'indirizzo…",
    /** Indirizzo libero. */
    available: 'Indirizzo disponibile.',
    /** Controllo non riuscito: la creazione resta possibile. */
    unknown: "Controllo non riuscito. Puoi creare il progetto: l'indirizzo si verifica alla creazione.",
    /** Motivi dell'indisponibilità. */
    reason: {
      /** Usato da un altro progetto o sito. */
      taken: 'Indirizzo già usato da un altro progetto. Scegline un altro.',
      /** Nome riservato della piattaforma. */
      reserved: 'Indirizzo riservato. Scegline un altro.',
      /** Forma non valida. */
      invalid: 'Indirizzo non valido. Usa da 3 a 40 caratteri fra minuscole, cifre e trattini, senza «-demo» in fondo.',
    },
    /** Azione che usa il nome proposto dal server. */
    useSuggestion: 'Usa {{suggestion}}',
    /** Campo vuoto all'invio. */
    missing: "Scrivi l'indirizzo del sito.",
    /** Rifiuto 409 `subdomain_taken` della creazione. */
    takenOnCreate: 'Indirizzo già usato. Scegline un altro.',
  },

  /** Scheda «Dipendenze». */
  links: {
    /** Titolo della scheda. */
    title: 'Dipendenze fra progetti',
    /** Pulsante che apre il cassetto di dichiarazione. */
    create: 'Nuova dipendenza',
    /**
     * Che cosa è e che cosa non è una dipendenza.
     *
     * Il marcatore `<strong>` è l'elemento passato a `Trans`: l'enfasi cambia
     * il significato della frase e non si può togliere.
     */
    intro:
      'Dichiarare una dipendenza documenta che questo progetto usa le API di un altro. <strong>Non concede alcun accesso.</strong> Perché gli agenti raggiungano l\'altro progetto serve un permesso, nella scheda Accessi.',
    /** Titolo della tabella delle dipendenze uscenti. */
    outgoingTitle: 'Dipendenze dichiarate',
    /** Titolo della tabella delle dipendenze entranti. */
    incomingTitle: 'Progetti che dipendono da questo',
    /** Colonne delle due tabelle. */
    columns: {
      /** Progetto di cui si usano le API. */
      target: 'Progetto usato',
      /** Progetto che dichiara di dipendere da questo. */
      source: 'Progetto che dipende',
      /** Quali API e per farne che cosa. */
      notes: 'API usate e scopo',
      /** Chi ha dichiarato la dipendenza. */
      declaredBy: 'Dichiarata da',
      /** Quando è stata dichiarata. */
      declaredAt: 'Dichiarata il',
    },
    /** Nessuna dipendenza uscente. */
    emptyOutgoing: 'Nessuna dipendenza dichiarata.',
    /** Nessuna dipendenza entrante. */
    emptyIncoming: 'Nessun progetto dichiara di dipendere da questo.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento delle dipendenze non riuscito. Riprova.',
    /** Rimozione di una dipendenza. */
    remove: {
      /** Etichetta del pulsante. */
      label: 'Rimuovi',
      /** Nome accessibile della riga. */
      accessibleName: 'Rimuovi la dipendenza da {{name}}',
      /** Domanda della conferma. */
      question: 'Rimuovere la dipendenza da {{name}}?',
      /** Che cosa succede davvero. */
      consequence:
        'Sparisce la documentazione della dipendenza. I permessi già concessi restano come sono.',
      /** Esito riuscito. */
      done: 'Dipendenza rimossa.',
    },
    /** Cassetto di dichiarazione. */
    form: {
      /** Titolo del cassetto e nome accessibile del form. */
      title: 'Nuova dipendenza',
      /** Pulsante di invio. */
      submit: 'Dichiara dipendenza',
      /** Etichetta del selettore del progetto. */
      target: 'Progetto usato',
      /** Testo di riempimento del selettore. */
      targetPlaceholder: 'Scegli un progetto',
      /** Etichetta del campo delle note. */
      notes: 'API usate e scopo',
      /** Che cosa scrivere nelle note. */
      notesHelp: 'Scrivi quali API usa e a che scopo.',
      /** Esito riuscito. */
      done: 'Dipendenza dichiarata.',
    },
  },

  /** Scheda «Accessi». */
  grants: {
    /** Titolo della scheda. */
    title: 'Accessi',
    /** Pulsante che apre il cassetto del nuovo permesso. */
    create: 'Nuovo permesso',
    /**
     * Che cosa dice l'elenco dei permessi.
     *
     * Il marcatore `<strong>` è l'elemento passato a `Trans`: «solo» in
     * grassetto distingue un accesso concesso da un accesso supposto.
     */
    intro:
      'Chi può leggere o scrivere su questo progetto. Vale per utenti e agenti: un agente di un altro progetto vede questo <strong>solo</strong> se compare qui.',
    /** Colonne della tabella. */
    columns: {
      /** Utente o agente che ha il permesso. */
      beneficiary: 'Beneficiario',
      /** Azione concessa. */
      permission: 'Permesso',
      /** Chi ha concesso il permesso. */
      grantedBy: 'Concesso da',
      /** Quando è stato concesso. */
      grantedAt: 'Concesso il',
      /** Motivo registrato al momento della concessione. */
      reason: 'Motivo',
    },
    /** Etichetta di un beneficiario che non è un agente del progetto. */
    user: 'Utente',
    /** Nessun permesso esplicito. */
    empty: 'Nessun permesso concesso. Chi ha creato il progetto vi accede sempre.',
    /** Permesso mancante per leggere o modificare gli accessi. */
    forbidden: 'Per vedere e modificare gli accessi devi amministrare questo progetto.',
    /** Lettura degli agenti del progetto non riuscita. */
    agentsLoadFailed: 'Caricamento degli agenti non riuscito. Riprova.',
    /** Revoca di un permesso. */
    revoke: {
      /** Etichetta del pulsante. */
      label: 'Revoca',
      /** Nome accessibile della riga. */
      accessibleName: 'Revoca il permesso di {{name}}',
      /** Domanda della conferma. */
      question: 'Revocare il permesso di {{name}} su {{project}}?',
      /** Che cosa succede davvero, con l'azione in minuscolo dentro la frase. */
      consequence: "Il beneficiario perde subito l'accesso in {{action}}.",
      /** Esito riuscito. */
      done: 'Permesso revocato.',
    },
    /** Cassetto del nuovo permesso. */
    form: {
      /** Titolo del cassetto e nome accessibile del form. */
      title: 'Nuovo permesso',
      /** Pulsante di invio. */
      submit: 'Concedi',
      /** Etichetta della scelta fra agente e utente. */
      kind: 'Tipo di beneficiario',
      /** Opzione «agente del progetto». */
      kindAgent: 'Agente di questo progetto',
      /** Opzione «utente». */
      kindUser: 'Utente',
      /** Etichetta del selettore dell'agente. */
      agent: 'Agente',
      /** Quali agenti compaiono nel selettore. */
      agentHelp: 'Gli agenti con origine in questo progetto.',
      /** Testo di riempimento del selettore. */
      agentPlaceholder: 'Scegli un agente',
      /** Selettore senza opzioni. */
      agentNotFound: 'Nessun agente in questo progetto',
      /** Opzione di un agente sospeso. */
      agentSuspended: '{{name}} (sospeso)',
      /** Etichetta del campo dell'identificativo di un utente. */
      userId: "Identificativo dell'utente",
      /** Dove trovare l'identificativo, finché non c'è la ricerca per nome. */
      userIdHelp:
        "Lo trovi nella scheda Accessi di un progetto in cui l'utente compare già. La ricerca per nome non è ancora disponibile.",
      /** Etichetta del selettore dell'azione. */
      permission: 'Permesso',
      /** Voce del selettore per l'amministrazione, che vale solo per gli utenti. */
      adminOption: 'Amministrazione (solo utenti)',
      /** Etichetta del campo del motivo. */
      reason: 'Motivo',
      /** Dove finisce il motivo. */
      reasonHelp: 'Resta nel registro.',
      /** Titolo dell'avviso sull'amministrazione. */
      adminWarningTitle: "L'amministrazione vale solo per gli utenti",
      /** Corpo dell'avviso sull'amministrazione. */
      adminWarningBody:
        'Un agente non amministra e non concede mai. Concessa a un agente, la richiesta viene rifiutata.',
      /** Esito riuscito. */
      done: 'Permesso concesso.',
    },
  },
};

/** Forma dei testi dei progetti, usata per tipare le altre lingue. */
export type Projects = typeof projects;
